/**
 * Ask — natural-language warehouse queries.
 *
 * The LLM only translates the question into a semantic query; every number
 * in the answer is computed by the warehouse. The resolved query ships
 * with each answer so the translation is always inspectable.
 */

import { useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Page,
  Text,
  TextField,
} from '@shopify/polaris';

import { ask, listAskMetrics } from '../services/marketingSuiteApi';
import type { AskResponse, MetricInfo } from '../types/marketingSuite';
import { SimpleMarkdown } from '../components/marketingSuite/SimpleMarkdown';
import { LineChart } from '../components/marketingSuite/MiniChart';

const EXAMPLES = [
  'How is net ROAS trending weekly?',
  'What did we spend on ads last month?',
  'Show conversions by platform this week',
  'Is our email engagement rate holding?',
];

interface HistoryEntry {
  question: string;
  response: AskResponse;
}

export default function Ask() {
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [metrics, setMetrics] = useState<MetricInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listAskMetrics()
      .then((r) => setMetrics(r.metrics))
      .catch(() => undefined);
  }, []);

  const submit = async (q?: string) => {
    if (loading) return; // one translation+query at a time
    const text = (q ?? question).trim();
    if (!text) return;
    setLoading(true);
    setError(null);
    try {
      const response = await ask(text);
      setHistory((prev) => [{ question: text, response }, ...prev]);
      setQuestion('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ask failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page
      title="Ask your data"
      subtitle="Questions become semantic queries; the warehouse computes every number."
    >
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="300">
            <TextField
              label="Question"
              labelHidden
              placeholder="e.g. How is net ROAS trending weekly?"
              value={question}
              onChange={setQuestion}
              autoComplete="off"
              connectedRight={
                <Button
                  variant="primary"
                  loading={loading}
                  onClick={() => void submit()}
                >
                  Ask
                </Button>
              }
            />
            <InlineStack gap="200">
              {EXAMPLES.map((example) => (
                <Button
                  key={example}
                  size="slim"
                  variant="tertiary"
                  disabled={loading}
                  onClick={() => void submit(example)}
                >
                  {example}
                </Button>
              ))}
            </InlineStack>
          </BlockStack>
        </Card>
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {history.map((entry, index) => (
          <Card key={`${entry.question}-${index}`}>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h3" variant="headingMd">
                  {entry.question}
                </Text>
                <Badge tone="info">
                  {`query: ${String(entry.response.resolved_query.metric)} / ${String(entry.response.resolved_query.period_type ?? 'weekly')}`}
                </Badge>
              </InlineStack>
              <SimpleMarkdown text={entry.response.answer_md} />
              {entry.response.result.rows.length > 1 &&
                !entry.response.resolved_query.breakdown && (
                  <LineChart
                    rows={entry.response.result.rows}
                    unit={entry.response.result.unit}
                  />
                )}
            </BlockStack>
          </Card>
        ))}
        {history.length === 0 && metrics.length > 0 && (
          <Card>
            <BlockStack gap="200">
              <Text as="h3" variant="headingMd">
                What you can ask about
              </Text>
              <InlineStack gap="200" wrap>
                {metrics.map((m) => (
                  <Badge key={m.key}>{m.label}</Badge>
                ))}
              </InlineStack>
              <Text as="p" tone="subdued">
                Ask requires your organization&apos;s LLM key (Settings →
                LLM) for translation; the numbers always come from the
                warehouse either way.
              </Text>
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}
